import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Code, FileDown, Loader2, RefreshCw } from 'lucide-react';
import { EnPowerLogo } from './EnPowerLogo';

const DEFAULT_TYPES = [
  { key: 'dachpv', label: 'Dach PV', requiresDeal: true, supportsCode: false, enabled: true },
  { key: 'ffpv', label: 'Freiflächen PV', requiresDeal: true, supportsCode: false, enabled: true },
  { key: 'pv_projekt_expose', label: 'Exposé Projektrechte', requiresDeal: true, supportsCode: false, enabled: true },
  { key: 'pv_turnkey_expose', label: 'Exposé Turnkey Verkauf', requiresDeal: true, supportsCode: false, enabled: true },
  { key: 'kleinspeicher', label: 'Kleinspeicher', requiresDeal: true, supportsCode: false, enabled: true },
  { key: 'bess', label: 'Großspeicher', requiresDeal: true, supportsCode: false, enabled: true },
  { key: 'sanierung', label: 'Sanierung', requiresDeal: true, supportsCode: false, enabled: true },
  { key: 'enpower_vertrieb', label: 'EN:POWER Vertriebspräsentation', requiresDeal: false, supportsCode: true, enabled: true },
];

function getFilenameFromDisposition(disposition, fallback) {
  const match = String(disposition || '').match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
  const raw = match?.[1] || match?.[2] || fallback;

  try {
    return decodeURIComponent(raw);
  } catch (_error) {
    return raw;
  }
}

async function parseDownloadError(response) {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    const payload = await response.json().catch(() => ({}));
    return payload.detail || payload.message || `Request fehlgeschlagen (${response.status}).`;
  }

  const text = await response.text().catch(() => '');
  return text || `Request fehlgeschlagen (${response.status}).`;
}

async function saveResponseAsDownload(response, fallbackFilename) {
  if (!response.ok) {
    throw new Error(await parseDownloadError(response));
  }

  const blob = await response.blob();
  const filename = getFilenameFromDisposition(response.headers.get('content-disposition'), fallbackFilename);
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);

  return filename;
}

function buildStatusStyle(kind) {
  if (kind === 'success') {
    return { color: '#166534', background: '#DCFCE7', borderColor: '#86EFAC' };
  }

  if (kind === 'error') {
    return { color: '#991B1B', background: '#FEE2E2', borderColor: '#FCA5A5' };
  }

  return { color: '#334155', background: '#F8FAFC', borderColor: '#CBD5E1' };
}

export default function OfferGeneratorPage({ apiBase = '/api', authHeaders = {} }) {
  const [offerTypes, setOfferTypes] = useState(DEFAULT_TYPES);
  const [selectedTypeKey, setSelectedTypeKey] = useState(DEFAULT_TYPES[0].key);
  const [dealId, setDealId] = useState('');
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  const [busyAction, setBusyAction] = useState('');
  const [status, setStatus] = useState({ kind: 'idle', text: '' });
  const publicHost = typeof window !== 'undefined' ? window.location.host : 'frontend-kohl-tau-4laa95qscn.vercel.app';

  const selectedType = useMemo(
    () => offerTypes.find((type) => type.key === selectedTypeKey) || offerTypes[0] || DEFAULT_TYPES[0],
    [offerTypes, selectedTypeKey]
  );

  const loadTypes = useCallback(async () => {
    setIsLoadingTypes(true);

    try {
      const response = await fetch(`${apiBase}/generator/generate-offer?action=types`, {
        headers: authHeaders,
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(await parseDownloadError(response));
      }

      const payload = await response.json();
      const nextTypes = Array.isArray(payload.types) && payload.types.length > 0 ? payload.types : DEFAULT_TYPES;
      setOfferTypes(nextTypes);

      if (!nextTypes.some((type) => type.key === selectedTypeKey)) {
        setSelectedTypeKey(nextTypes[0].key);
      }
    } catch (error) {
      console.error(error);
      setStatus({ kind: 'error', text: error.message || 'Generator-Konfiguration konnte nicht geladen werden.' });
    } finally {
      setIsLoadingTypes(false);
    }
  }, [apiBase, authHeaders, selectedTypeKey]);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  const runDownload = useCallback(async (endpoint, fallbackFilename, actionName) => {
    if (selectedType.requiresDeal && !dealId.trim()) {
      setStatus({ kind: 'error', text: 'Bitte eine Deal-ID eintragen.' });
      return;
    }

    if (selectedType.enabled === false) {
      setStatus({ kind: 'error', text: 'Für diesen Dokumenttyp fehlt die Erzeugen-Berechtigung.' });
      return;
    }

    setBusyAction(actionName);
    setStatus({ kind: 'idle', text: '' });

    try {
      const response = await fetch(`${apiBase}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify({
          dealId: dealId.trim(),
          offerType: selectedType.key,
        }),
      });

      const filename = await saveResponseAsDownload(response, fallbackFilename);
      setStatus({ kind: 'success', text: `${filename} wurde erstellt.` });
    } catch (error) {
      console.error(error);
      setStatus({ kind: 'error', text: error.message || 'Download konnte nicht erstellt werden.' });
    } finally {
      setBusyAction('');
    }
  }, [apiBase, authHeaders, dealId, selectedType]);

  const handlePdfDownload = () => {
    runDownload('/generator/generate-offer', 'ENPOWER_Angebot.pdf', 'pdf');
  };

  const handleCodeDownload = () => {
    runDownload('/generator/generate-code', 'ENPOWER_Vertrieb.html', 'code');
  };

  const statusStyle = buildStatusStyle(status.kind);
  const isBusy = Boolean(busyAction);

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: '#F8FAFC' }}>
      <div style={{ maxWidth: '980px', margin: '0 auto', padding: '28px 24px 42px', display: 'grid', gap: '18px' }}>
        <section style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'grid', gap: '8px' }}>
            <EnPowerLogo size="md" variant="color" />
            <div style={{ display: 'grid', gap: '4px' }}>
              <h1 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: '30px', lineHeight: 1.1, color: '#122A5C', fontWeight: 700, letterSpacing: '-0.04em' }}>
                Offergenerator
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontFamily: 'Manrope, sans-serif', fontSize: '12px', fontWeight: 700, color: '#166534', background: '#DCFCE7', border: '1px solid #86EFAC', borderRadius: '999px', padding: '6px 10px' }}>
                  <CheckCircle2 size={14} />
                  Bitrix-Automatik aktiv
                </span>
                <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '12px', fontWeight: 700, color: '#64748B' }}>
                  Hauptsystem: {publicHost}
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={loadTypes}
            disabled={isLoadingTypes || isBusy}
            className="btn-docx"
            title="Generator-Konfiguration neu laden"
          >
            {isLoadingTypes ? <Loader2 size={14} className="spin-icon" /> : <RefreshCw size={14} />}
            <span>{isLoadingTypes ? 'Lädt...' : 'Aktualisieren'}</span>
          </button>
        </section>

        <section style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '18px', padding: '22px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
              <label style={{ display: 'grid', gap: '7px', fontFamily: 'Manrope, sans-serif', fontSize: '12px', fontWeight: 700, color: '#334155' }}>
                Angebotsart
                <select
                  value={selectedType.key}
                  onChange={(event) => setSelectedTypeKey(event.target.value)}
                  disabled={isBusy}
                  style={{ width: '100%', height: '42px', border: '1px solid #CBD5E1', borderRadius: '8px', padding: '0 12px', fontFamily: 'Manrope, sans-serif', fontSize: '14px', color: '#0F172A', background: '#FFFFFF' }}
                >
                  {offerTypes.map((type) => (
                    <option key={type.key} value={type.key}>
                      {type.enabled === false ? `${type.label} (kein Zugriff)` : type.label}
                    </option>
                  ))}
                </select>
              </label>

              {selectedType.requiresDeal && (
                <label style={{ display: 'grid', gap: '7px', fontFamily: 'Manrope, sans-serif', fontSize: '12px', fontWeight: 700, color: '#334155' }}>
                  Deal ID
                  <input
                    type="text"
                    value={dealId}
                    onChange={(event) => setDealId(event.target.value)}
                    disabled={isBusy}
                    placeholder="D_7086"
                    style={{ width: '100%', height: '42px', border: '1px solid #CBD5E1', borderRadius: '8px', padding: '0 12px', fontFamily: 'Manrope, sans-serif', fontSize: '14px', color: '#0F172A', background: '#FFFFFF' }}
                  />
                </label>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handlePdfDownload}
                disabled={isBusy || selectedType.enabled === false}
                className="btn-pdf"
                title="PDF herunterladen"
              >
                {busyAction === 'pdf' ? <Loader2 size={14} className="spin-icon" /> : <FileDown size={14} />}
                <span>{busyAction === 'pdf' ? 'PDF wird erstellt...' : 'PDF herunterladen'}</span>
              </button>

              {selectedType.supportsCode && (
                <button
                  type="button"
                  onClick={handleCodeDownload}
                  disabled={isBusy || selectedType.enabled === false}
                  className="btn-docx"
                  title="HTML-Code herunterladen"
                >
                  {busyAction === 'code' ? <Loader2 size={14} className="spin-icon" /> : <Code size={14} />}
                  <span>{busyAction === 'code' ? 'Code wird erstellt...' : 'HTML-Code herunterladen'}</span>
                </button>
              )}
            </div>

            {status.text && (
              <div style={{ fontFamily: 'Manrope, sans-serif', fontSize: '13px', fontWeight: 700, border: '1px solid', borderRadius: '8px', padding: '10px 12px', ...statusStyle }}>
                {status.text}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
